import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import AppShell from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { formatApiError } from "@/lib/utils-app";
import { DownloadSimpleIcon as DownloadSimple, WarningIcon as Warning, ShieldCheckIcon as ShieldCheck, ArrowCounterClockwiseIcon as ArrowCounterClockwise } from "@phosphor-icons/react";

/**
 * /settings/privacy — GDPR Art. 15 / Art. 17 compliance surface.
 *
 * Two things this page does:
 *   1. "Download my data" → triggers the streaming-ZIP export.
 *   2. "Delete my account" → schedules a 30-day soft delete (with
 *      verbatim confirmation phrase friction) and shows a recovery
 *      banner during the grace window.
 */
export default function PrivacyDataPage() {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [phrase, setPhrase] = useState("");

  const loadStatus = async () => {
    try {
      const { data } = await api.get("/users/me/deletion-status");
      setStatus(data);
    } catch {
      setStatus({ pending: false });
    }
  };

  useEffect(() => { loadStatus(); }, []);

  const downloadExport = async () => {
    setBusy(true);
    try {
      const res = await api.get("/users/me/export", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/zip" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `armstrong-k9-export.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Your data download has started.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const requestDeletion = async () => {
    setBusy(true);
    try {
      await api.post("/users/me/request-deletion", {
        confirmation_phrase: phrase,
      });
      try {
        const m = await import("@/lib/nativeAuth");
        await m.clearBiometricSession();
      } catch { /* web or old binary */ }
      toast.success("Account scheduled for deletion. You have 30 days to change your mind.");
      setConfirmOpen(false);
      setPhrase("");
      loadStatus();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const cancelDeletion = async () => {
    setBusy(true);
    try {
      await api.post("/users/me/cancel-deletion");
      toast.success("Deletion cancelled. Welcome back.");
      loadStatus();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AppShell>
      <section className="max-w-3xl mx-auto px-4 sm:px-6 py-8 space-y-8" data-testid="privacy-page">
        <header>
          <h1 className="font-spectral text-3xl sm:text-4xl tracking-tight">Privacy & your data</h1>
          <p className="text-[#6B6B6B] mt-3 leading-relaxed">
            You can export every record we hold about you, or schedule
            your account for deletion. Per GDPR Art. 15 and CCPA, this
            is your right.
          </p>
        </header>

        {/* Pending-deletion banner */}
        {status?.pending && (
          <div className="rounded-xl border-2 border-crimson bg-[#FFF0F0] p-5" data-testid="deletion-pending-banner">
            <div className="flex items-start gap-3">
              <Warning size={24} weight="fill" className="text-crimson mt-0.5" />
              <div className="flex-1">
                <h2 className="font-spectral text-xl text-crimson">Your account is scheduled for deletion</h2>
                <p className="mt-2 text-[14px] text-[#7A1A1A] leading-relaxed">
                  <strong>{status.days_remaining} day{status.days_remaining === 1 ? "" : "s"} remaining</strong> before permanent purge ({status.purge_at?.slice(0, 10)}).
                  Active subscriptions will cancel at period end. Your kennel page is already hidden from the public.
                </p>
                <Button
                  onClick={cancelDeletion}
                  disabled={busy}
                  className="mt-4 bg-ink hover:bg-black text-white rounded-full"
                  data-testid="cancel-deletion-btn"
                >
                  <ArrowCounterClockwise size={14} weight="bold" /> Cancel deletion & restore
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Data export */}
        <div className="rounded-xl border border-[#EAEAEA] bg-white p-6">
          <div className="flex items-start gap-3">
            <DownloadSimple size={24} weight="duotone" className="text-gold mt-0.5" />
            <div className="flex-1">
              <h2 className="font-spectral text-xl">Download my data</h2>
              <p className="mt-2 text-[14px] text-[#3F3F3F] leading-relaxed">
                A ZIP containing every record tied to your account: profile,
                dogs, litters, messages, payments, subscriptions, video tours,
                contracts, breeder profile, storefront, and consents. Your
                password hash and 2FA secret are never included.
              </p>
              <Button
                onClick={downloadExport}
                disabled={busy}
                className="mt-4 bg-ink hover:bg-crimson text-white rounded-full"
                data-testid="export-data-btn"
              >
                <DownloadSimple size={14} weight="bold" /> Download my data (ZIP)
              </Button>
            </div>
          </div>
        </div>

        {/* Delete account */}
        {!status?.pending && (
          <div className="rounded-xl border-2 border-crimson/30 bg-white p-6" data-testid="delete-account-block">
            <div className="flex items-start gap-3">
              <ShieldCheck size={24} weight="duotone" className="text-crimson mt-0.5" />
              <div className="flex-1">
                <h2 className="font-spectral text-xl text-crimson">Delete my account</h2>
                <p className="mt-2 text-[14px] text-[#3F3F3F] leading-relaxed">
                  Hides your profile and kennel immediately. Permanent deletion
                  happens 30 days later — you can cancel any time before then by
                  signing back in. Active subscriptions auto-cancel at period
                  end (no further charges). Dog registration rows you created
                  remain in the registry so other dogs' pedigrees aren't broken;
                  your name is replaced with "[Account deleted]".
                </p>

                {!confirmOpen && (
                  <Button
                    onClick={() => setConfirmOpen(true)}
                    variant="outline"
                    className="mt-4 rounded-full border-crimson text-crimson hover:bg-crimson hover:text-white"
                    data-testid="open-delete-btn"
                  >
                    Start account deletion…
                  </Button>
                )}

                {confirmOpen && (
                  <div className="mt-4 rounded-xl border border-crimson/40 bg-[#FFF8F8] p-4 space-y-3" data-testid="delete-confirm-form">
                    <Label className="text-[12px] font-bold text-crimson">
                      Type DELETE MY ACCOUNT exactly to confirm:
                    </Label>
                    <Input
                      value={phrase}
                      onChange={(e) => setPhrase(e.target.value)}
                      placeholder="DELETE MY ACCOUNT"
                      data-testid="delete-confirm-input"
                    />
                    <div className="flex gap-2">
                      <Button
                        onClick={requestDeletion}
                        disabled={busy || phrase.trim().toUpperCase() !== "DELETE MY ACCOUNT"}
                        className="bg-crimson hover:bg-[#7A1A1A] text-white rounded-full"
                        data-testid="delete-confirm-submit"
                      >
                        Schedule deletion
                      </Button>
                      <Button
                        onClick={() => { setConfirmOpen(false); setPhrase(""); }}
                        variant="outline"
                        className="rounded-full"
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </section>
    </AppShell>
  );
}
